/**
 * NetworkStatusBanner — Shows offline indicator with sync queue count.
 *
 * Renders an amber banner when offline and a brief green toast on reconnection.
 * Uses the existing useOnlineStatus hook.
 *
 * @since 1.0.0
 */

import { useState, useEffect, useRef } from 'react';
import { WifiOff, Wifi, CloudUpload, Loader2 } from 'lucide-react';
import { getPendingCount } from '../../db/offline';

export function NetworkStatusBanner() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [showReconnected, setShowReconnected] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const wasOffline = useRef(false);

    useEffect(() => {
        const goOnline = () => {
            setIsOnline(true);
            if (wasOffline.current) {
                setShowReconnected(true);
                setTimeout(() => setShowReconnected(false), 3000);
            }
            wasOffline.current = false;
        };

        const goOffline = () => {
            setIsOnline(false);
            wasOffline.current = true;
        };

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);

        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    // Poll pending count when offline
    useEffect(() => {
        if (isOnline && !showReconnected) return;

        const poll = async () => {
            const count = await getPendingCount();
            setPendingCount(count);
        };

        poll();
        const interval = setInterval(poll, 3000);
        return () => clearInterval(interval);
    }, [isOnline, showReconnected]);

    // Online and no reconnection toast → render nothing
    if (isOnline && !showReconnected) return null;

    // Reconnected toast
    if (isOnline && showReconnected) {
        return (
            <div className="bg-green-600 text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top-2">
                <Wifi size={16} />
                <span>Back online</span>
                {pendingCount > 0 && (
                    <>
                        <span className="text-green-200">·</span>
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-green-100">Syncing {pendingCount} change{pendingCount !== 1 ? 's' : ''}…</span>
                    </>
                )}
            </div>
        );
    }

    // Offline banner
    return (
        <div className="bg-teal-700 text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
            <WifiOff size={16} />
            {/* flow-1: the old words promised a sync queue that nothing feeds.
                What is true: a half-typed answer is kept, and posting waits. */}
            <span>You're offline. Karr keeps what you type; post when you are back on.</span>
            {pendingCount > 0 && (
                <>
                    <span className="text-teal-300">·</span>
                    <CloudUpload size={14} />
                    <span className="text-teal-100 font-semibold">{pendingCount} pending</span>
                </>
            )}
        </div>
    );
}
